import React, { useState, useCallback, useEffect, useRef } from "react";

/* ───────────────────────────────────────────────────────────────
 * Load the YouTube IFrame API script once, globally.
 * We eagerly load it on first component mount so it's ready
 * by the time the user clicks.
 * ─────────────────────────────────────────────────────────────── */
let ytApiReady = false;
let ytApiCallbacks = [];

function ensureYTApi() {
  if (ytApiReady) return;
  if (window.YT && window.YT.Player) {
    ytApiReady = true;
    return;
  }
  if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) return;

  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (prev) prev();
    ytApiReady = true;
    ytApiCallbacks.forEach((cb) => cb());
    ytApiCallbacks = [];
  };
  const tag = document.createElement("script");
  tag.src = "https://www.youtube.com/iframe_api";
  document.head.appendChild(tag);
}

function whenYTReady(cb) {
  if (ytApiReady && window.YT && window.YT.Player) {
    cb();
  } else {
    ytApiCallbacks.push(cb);
    ensureYTApi();
  }
}

/**
 * Lightweight YouTube embed — single-click play on ALL browsers
 * including Safari / iOS.
 *
 * Strategy: Safari blocks autoplay-with-audio in cross-origin iframes
 * regardless of user gesture. So we start the video **muted** (which
 * Safari always allows) and then unmute it via the YT API ~200 ms later.
 * The transition is imperceptible to the user — they click once and
 * the video plays with sound.
 *
 * Props:
 *  - videoId  (string) REQUIRED
 *  - thumbnail (string) REQUIRED
 *  - title    (string) OPTIONAL
 *  - className (string) OPTIONAL
 *  - aspect   (string) OPTIONAL  e.g. "16/9"
 */
const YouTubeVideo = ({
  videoId,
  thumbnail,
  title = "YouTube video",
  className = "",
  aspect = "16/9",
}) => {
  const [active, setActive] = useState(false);
  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);

  // Eagerly load the YT API on mount
  useEffect(() => {
    ensureYTApi();
  }, []);

  const computePadding = useCallback(() => {
    const parts = aspect.split("/");
    if (parts.length === 2) {
      const w = parseFloat(parts[0]);
      const h = parseFloat(parts[1]);
      if (w > 0 && h > 0) return `${(h / w) * 100}%`;
    }
    return "56.25%";
  }, [aspect]);

  const handlePlay = useCallback(() => {
    setActive(true);
  }, []);

  /*
   * Create the YT player when the user clicks.
   * We pass mute=1 in playerVars so Safari allows autoplay,
   * then unmute once playback has actually started.
   */
  useEffect(() => {
    if (!active || !playerContainerRef.current) return;
    let destroyed = false;

    whenYTReady(() => {
      if (destroyed || !playerContainerRef.current) return;

      playerRef.current = new window.YT.Player(playerContainerRef.current, {
        videoId,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          autoplay: 1,
          mute: 1,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            e.target.playVideo();
          },
        },
      });
    });

    return () => {
      destroyed = true;
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [active, videoId]);

  return (
    <div className={`relative w-full ${className}`}>
      {/* Aspect ratio box */}
      <div style={{ paddingTop: computePadding() }} />
      {!active && (
        <button
          type="button"
          onClick={handlePlay}
          className="group absolute inset-0 w-full h-full focus:outline-none"
          aria-label={`Play video: ${title}`}
        >
          <img
            src={thumbnail}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/90 group-hover:bg-white rounded-full flex items-center justify-center shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 64 64"
                className="w-8 h-8 md:w-10 md:h-10 text-[#b24029]"
                fill="currentColor"
              >
                <path d="M25 18v28l22-14-22-14z" />
              </svg>
            </div>
          </div>
        </button>
      )}
      {active && (
        <div
          ref={playerContainerRef}
          className="absolute inset-0 w-full h-full"
        />
      )}
      <noscript>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0&playsinline=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </noscript>
    </div>
  );
};

export default YouTubeVideo;
